import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { UserPlus, Pencil, KeyRound, ShieldCheck, X, Save, Power, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLE_TONE = {
  owner: "red", office_admin: "navy", sales_manager: "blue", sales: "blue",
  production_manager: "amber", project_manager: "amber", hr: "green", crew: "slate",
};
const inputCls = "mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="mb-card p-6 w-full max-w-md animate-scale-in my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl font-bold uppercase text-navy">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [add, setAdd] = useState(null);
  const [edit, setEdit] = useState(null);
  const [pw, setPw] = useState(null);
  const [myPw, setMyPw] = useState(null);

  const load = () => api.get("/auth/team").then((r) => { setMembers(r.data.members); setRoles(r.data.roles); }).catch(() => toast.error("Owner access required"));
  useEffect(() => { load(); }, []);

  const createMember = async () => {
    if (!add.name || !add.email || !add.password || !add.role) return toast.error("All fields are required");
    try { await api.post("/auth/team", add); toast.success("Team member added"); setAdd(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/auth/team/${edit.id}`, { name: edit.name, role: edit.role }); toast.success("Updated"); setEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async (m) => {
    try { await api.post(`/auth/team/${m.id}/status?active=${!m.active}`); toast.success(m.active ? "Deactivated" : "Reactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeMyPw = async () => {
    if (!myPw.current_password) return toast.error("Enter your current password");
    if (!myPw.new_password || myPw.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    try { await api.post("/auth/change-password", { current_password: myPw.current_password, new_password: myPw.new_password }); toast.success("Password changed"); setMyPw(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
  };

  const resetPw = async () => {
    if (!pw.password || pw.password.length < 4) return toast.error("Password too short");
    try { await api.post(`/auth/team/${pw.id}/reset-password`, { password: pw.password }); toast.success("Password reset"); setPw(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Team" subtitle="Add your crew and office staff, and assign roles. Owner/Admin access is reserved for Raymond and you."
        actions={<div className="flex gap-2"><Btn variant="ghost" onClick={() => setMyPw({ current_password: "", new_password: "" })} testid="change-my-pw-btn"><KeyRound className="w-4 h-4" /> Change Password</Btn><Btn variant="red" onClick={() => setAdd({ name: "", email: "", role: roles[0]?.value || "sales", password: "" })} testid="add-member-btn"><UserPlus className="w-4 h-4" /> Add Member</Btn></div>} />

      <div className="mb-card overflow-x-auto mb-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Added</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid={`member-${m.id}`} className={`border-t border-slate-100 ${!m.active ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-semibold text-navy flex items-center gap-2">
                  {m.name}{m.protected && <Lock className="w-3.5 h-3.5 text-brandred" title="Protected owner/admin" />}
                </td>
                <td className="px-4 py-3 text-slate-600">{m.email}</td>
                <td className="px-4 py-3"><Pill tone={ROLE_TONE[m.role] || "slate"}>{m.role_label}</Pill></td>
                <td className="px-4 py-3">{m.active ? <Pill tone="green">Active</Pill> : <Pill tone="slate">Inactive</Pill>}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(m.created_at)}</td>
                <td className="px-4 py-3">
                  {m.protected ? (
                    <span className="flex items-center justify-end gap-1 text-xs text-slate-400"><ShieldCheck className="w-4 h-4 text-brandred" /> Admin</span>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-member-${m.id}`} onClick={() => setEdit({ id: m.id, name: m.name, role: m.role })} className="text-slate-400 hover:text-navy p-1.5" title="Edit role"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`pw-member-${m.id}`} onClick={() => setPw({ id: m.id, password: "" })} className="text-slate-400 hover:text-navy p-1.5" title="Reset password"><KeyRound className="w-4 h-4" /></button>
                      <button data-testid={`status-member-${m.id}`} onClick={() => toggleStatus(m)} className={`p-1.5 ${m.active ? "text-slate-400 hover:text-dangerr" : "text-slate-400 hover:text-successg"}`} title={m.active ? "Deactivate" : "Reactivate"}><Power className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {add && (
        <Modal title="Add Team Member" onClose={() => setAdd(null)}>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name</label>
              <input data-testid="member-name" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input data-testid="member-email" type="email" value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</label>
              <select data-testid="member-role" value={add.role} onChange={(e) => setAdd({ ...add, role: e.target.value })} className={inputCls}>
                {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Temporary Password</label>
              <input data-testid="member-password" value={add.password} onChange={(e) => setAdd({ ...add, password: e.target.value })} className={inputCls} placeholder="They can change it later" /></div>
          </div>
          <Btn variant="red" onClick={createMember} className="w-full mt-4" testid="save-member"><UserPlus className="w-4 h-4" /> Add Member</Btn>
        </Modal>
      )}

      {edit && (
        <Modal title="Edit Member" onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name</label>
              <input data-testid="edit-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</label>
              <select data-testid="edit-role" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className={inputCls}>
                {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select></div>
          </div>
          <Btn onClick={saveEdit} className="w-full mt-4" testid="save-edit-member"><Save className="w-4 h-4" /> Save</Btn>
        </Modal>
      )}

      {myPw && (
        <Modal title="Change My Password" onClose={() => setMyPw(null)}>
          <label className="text-xs font-semibold uppercase text-slate-500">Current password</label>
          <input type="password" data-testid="cur-pw-input" value={myPw.current_password} onChange={(e) => setMyPw({ ...myPw, current_password: e.target.value })} className={inputCls} />
          <label className="text-xs font-semibold uppercase text-slate-500 mt-3 block">New password</label>
          <input type="password" data-testid="new-pw-input" value={myPw.new_password} onChange={(e) => setMyPw({ ...myPw, new_password: e.target.value })} className={inputCls} placeholder="At least 6 characters" />
          <Btn onClick={changeMyPw} className="w-full mt-4" testid="save-my-pw"><KeyRound className="w-4 h-4" /> Update Password</Btn>
        </Modal>
      )}

      {pw && (
        <Modal title="Reset Password" onClose={() => setPw(null)}>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">New Password</label>
          <input data-testid="reset-pw-input" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} className={inputCls} />
          <Btn onClick={resetPw} className="w-full mt-4" testid="save-reset-pw"><KeyRound className="w-4 h-4" /> Set Password</Btn>
        </Modal>
      )}
    </div>
  );
}
